import { useCallback, useRef, useState } from 'react';

interface UseTouchHandlerOptions {
  onPress: () => void;
  debounceMs?: number;
}

export const useTouchHandler = ({ onPress, debounceMs = 300 }: UseTouchHandlerOptions) => {
  const [isPressed, setIsPressed] = useState(false);
  const lastPressTime = useRef(0);
  const touchStartTime = useRef(0);

  const handleTouchStart = useCallback(() => {
    touchStartTime.current = Date.now();
    setIsPressed(true);
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    const timeSinceLastPress = now - lastPressTime.current;
    const touchDuration = now - touchStartTime.current;

    if (timeSinceLastPress >= debounceMs && touchDuration < 1000) {
      lastPressTime.current = now;
      onPress();
    }

    setIsPressed(false);
  }, [onPress, debounceMs]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    const timeSinceLastPress = now - lastPressTime.current;

    if (timeSinceLastPress >= debounceMs) {
      lastPressTime.current = now;
      onPress();
    }
  }, [onPress, debounceMs]);

  return {
    isPressed,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onClick: handleClick,
    },
  };
};
